import { useState, useEffect, useCallback } from 'react';
import type { TripDetail, UpdateTripData } from '../types';
import * as tripService from '../services/tripService';

export interface UseTripReturn {
  trip: TripDetail | null;
  isLoading: boolean;
  error: string | null;
  updateTrip: (data: UpdateTripData) => Promise<TripDetail | null>;
  deleteTrip: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook that manages a single trip detail, including its pins and photos.
 * Fetches trip detail by ID and provides update/delete operations.
 */
export function useTrip(tripId: string | undefined): UseTripReturn {
  const [trip, setTrip] = useState<TripDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTrip = useCallback(async () => {
    if (!tripId) {
      setTrip(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await tripService.fetchTrip(tripId);
      setTrip(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip');
    } finally {
      setIsLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    loadTrip();
  }, [loadTrip]);

  const handleUpdateTrip = useCallback(async (data: UpdateTripData): Promise<TripDetail | null> => {
    if (!tripId) return null;
    const updated = await tripService.updateTrip(tripId, data);
    // Merge updated trip data with existing pins/photos
    setTrip((prev) => prev ? { ...prev, ...updated } : null);
    return trip ? { ...trip, ...updated } : null;
  }, [tripId, trip]);

  const handleDeleteTrip = useCallback(async (): Promise<void> => {
    if (!tripId) return;
    await tripService.deleteTrip(tripId);
    setTrip(null);
  }, [tripId]);

  return {
    trip,
    isLoading,
    error,
    updateTrip: handleUpdateTrip,
    deleteTrip: handleDeleteTrip,
    refresh: loadTrip,
  };
}
